import { useEffect, useMemo, useRef } from 'react';

import type {
  MomentsSavedHighlight,
  MomentsSavedHighlightSort,
} from '@/store/api/momentsApi';

import { MEMBER_REFLECTION_PROFILE_SLUGS } from '@/components/Sessions/memberReflectionProfiles';
import { useSearchMomentsSavedHighlightsQuery } from '@/store/api/momentsApi';

// The redesigned home page only uses the total for its footer link.
export const HOME_HIGHLIGHTS_LIMIT = 1;
export const SAVED_HIGHLIGHTS_PROFILE_SLUG = MEMBER_REFLECTION_PROFILE_SLUGS;

export type SavedHighlightsSort = MomentsSavedHighlightSort;
export type SavedHighlightsHomeStatus = 'error' | 'loading' | 'ready';

interface UseMemberSavedHighlightsHomeArgs {
  searchTerm?: string;
  sort: SavedHighlightsSort;
}

export const useMemberSavedHighlightsHome = ({
  searchTerm = '',
  sort,
}: UseMemberSavedHighlightsHomeArgs) => {
  const trimmedSearchTerm = searchTerm.trim();
  const highlightsArg = {
    direction: 'desc' as const,
    limit: HOME_HIGHLIGHTS_LIMIT,
    offset: 0,
    profileSlug: SAVED_HIGHLIGHTS_PROFILE_SLUG,
    q: trimmedSearchTerm || undefined,
    sort,
  };

  const { data, isError, isFetching, isLoading } =
    useSearchMomentsSavedHighlightsQuery(highlightsArg);
  const cachedDataRef =
    useRef<ReturnType<typeof useSearchMomentsSavedHighlightsQuery>['data']>(
      undefined,
    );

  useEffect(() => {
    if (data) {
      cachedDataRef.current = data;
    }
  }, [data]);

  const canUseCachedData = !isError && isFetching;
  const effectiveData =
    data ?? (canUseCachedData ? cachedDataRef.current : undefined);
  const highlights = useMemo<MomentsSavedHighlight[]>(
    () => effectiveData?.items ?? [],
    [effectiveData?.items],
  );

  let status: SavedHighlightsHomeStatus = 'ready';
  if (isError && !effectiveData) {
    status = 'error';
  } else if (isLoading && !effectiveData) {
    status = 'loading';
  }

  return {
    hasMore: effectiveData?.hasMore === true,
    highlights,
    isLoading: status === 'loading',
    isRefreshing: canUseCachedData && Boolean(effectiveData),
    status,
    totalCount: effectiveData?.totalCount ?? 0,
  };
};
